/**
 * Bed inventory API (B3 / S1–S7) — Ward → Room → Bed physical inventory.
 *
 * Backend endpoints (org resolved server-side from the session; every route is
 * behind B1's `require_admission_enabled` gate + `MANAGE_BED_CONFIG`):
 *
 *   Wards
 *     POST   /api/organisation/wards
 *     GET    /api/organisation/wards?include_inactive=&department_id=
 *     PATCH  /api/organisation/wards/:ward_id            (active:true = reactivate)
 *     DELETE /api/organisation/wards/:ward_id            (soft-delete)
 *     PUT    /api/organisation/wards/:ward_id/departments  (B6 — set "Visible to" tags)
 *   Rooms (incl. bays — is_bay=true room)
 *     POST   /api/organisation/wards/:ward_id/rooms
 *     GET    /api/organisation/rooms?include_inactive=   (flat org-wide list)
 *     PATCH  /api/organisation/wards/:ward_id/rooms/:room_id
 *     DELETE /api/organisation/wards/:ward_id/rooms/:room_id
 *   Beds (bulk-capable create)
 *     POST   /api/organisation/wards/:ward_id/rooms/:room_id/beds  → {beds:[...],count}
 *     GET    /api/organisation/beds?include_inactive=   (flat org-wide list)
 *     PATCH  /api/organisation/wards/:ward_id/rooms/:room_id/beds/:bed_id
 *     DELETE /api/organisation/wards/:ward_id/rooms/:room_id/beds/:bed_id
 *
 * Clone of bedCategoryApi.js: every function returns
 * `{ success, data?, error?, code? }`; `code` carries the server's
 * machine-readable business-rule code (WARD_NAME_TAKEN, DEPT_NOT_ACTIVE,
 * ROOM_NAME_TAKEN, BEDCAT_NOT_ACTIVE, NAME_REQUIRED_FOR_NON_BAY,
 * LABEL_WITH_BULK_COUNT, USE_DELETE_TO_DEACTIVATE, BED_LABEL_TAKEN, …) when the
 * 4xx `detail` is a `{code, message}` dict — the FE inline errors key on it.
 */

import apiClient from './apiClient';
import { parseCatalogError } from './bedCategoryApi';

const WARDS = '/api/organisation/wards';
const ROOMS = '/api/organisation/rooms';
const BEDS = '/api/organisation/beds';
const enc = encodeURIComponent;

// ── Wards ────────────────────────────────────────────────────────────────
// C1 / S3: the admit ward/bed picker anchors on the ADMISSION's department. A
// falsy `department_id` OMITS the param → server returns the FULL ward list
// (rule-(a) never-exclude, fail-open); a set anchor → B6 VISIBILITY-UNION
// (tagged ∪ untagged/ORG_WIDE ∪ dead-dept). Never a 4xx from the anchor.
export const listWards = async ({ include_inactive, department_id } = {}) => {
  try {
    const params = {};
    if (include_inactive) params.include_inactive = true;
    if (department_id) params.department_id = department_id;
    const response = await apiClient.get(WARDS, { params });
    return {
      success: true,
      data: {
        wards: response.data?.wards || [],
        count: response.data?.count ?? (response.data?.wards?.length || 0),
      },
    };
  } catch (error) {
    console.error('[BedInventoryAPI] list wards error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to load wards.') };
  }
};

export const createWard = async (body) => {
  try {
    const response = await apiClient.post(WARDS, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedInventoryAPI] create ward error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to create ward.') };
  }
};

export const updateWard = async (wardId, body) => {
  try {
    const response = await apiClient.patch(`${WARDS}/${enc(wardId)}`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedInventoryAPI] update ward error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to update ward.') };
  }
};

export const deactivateWard = async (wardId) => {
  try {
    const response = await apiClient.delete(`${WARDS}/${enc(wardId)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedInventoryAPI] deactivate ward error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to deactivate ward.') };
  }
};

/**
 * B6 / S1 — tag a ward with 0..N departments ("Visible to"). Clone of
 * bedCategoryApi.setBedCategoryDepartments. Empty array clears all edges →
 * owner_scope reverts to ORG_WIDE; ≥1 → owner_scope=DEPARTMENT. Server rejects
 * (422 `{code, message}`) an unknown (DEPARTMENT_NOT_FOUND) / inactive
 * (DEPARTMENT_INACTIVE) / racing (CONCURRENT_MODIFICATION) department.
 * Response = enriched WardResponse (owner_scope + department_ids).
 *
 * PUT /api/organisation/wards/:ward_id/departments
 * @param {string}   wardId
 * @param {string[]} department_ids
 * @returns {Promise<{success, data?, error?, code?}>}
 */
export const setWardDepartments = async (wardId, department_ids) => {
  try {
    const response = await apiClient.put(
      `${WARDS}/${enc(wardId)}/departments`,
      { department_ids: department_ids || [] },
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedInventoryAPI] setWardDepartments error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to save departments.') };
  }
};

// ── Rooms (incl. bays) ───────────────────────────────────────────────────
export const listRooms = async ({ include_inactive } = {}) => {
  try {
    const params = {};
    if (include_inactive) params.include_inactive = true;
    const response = await apiClient.get(ROOMS, { params });
    return {
      success: true,
      data: {
        rooms: response.data?.rooms || [],
        count: response.data?.count ?? (response.data?.rooms?.length || 0),
      },
    };
  } catch (error) {
    console.error('[BedInventoryAPI] list rooms error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to load rooms.') };
  }
};

export const createRoom = async (wardId, body) => {
  try {
    const response = await apiClient.post(`${WARDS}/${enc(wardId)}/rooms`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedInventoryAPI] create room error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to create room.') };
  }
};

export const updateRoom = async (wardId, roomId, body) => {
  try {
    const response = await apiClient.patch(`${WARDS}/${enc(wardId)}/rooms/${enc(roomId)}`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedInventoryAPI] update room error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to update room.') };
  }
};

export const deactivateRoom = async (wardId, roomId) => {
  try {
    const response = await apiClient.delete(`${WARDS}/${enc(wardId)}/rooms/${enc(roomId)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedInventoryAPI] deactivate room error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to deactivate room.') };
  }
};

// ── Beds (bulk-capable) ──────────────────────────────────────────────────
export const listBeds = async ({ include_inactive } = {}) => {
  try {
    const params = {};
    if (include_inactive) params.include_inactive = true;
    const response = await apiClient.get(BEDS, { params });
    return {
      success: true,
      data: {
        beds: response.data?.beds || [],
        count: response.data?.count ?? (response.data?.beds?.length || 0),
      },
    };
  } catch (error) {
    console.error('[BedInventoryAPI] list beds error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to load beds.') };
  }
};

/** POST beds → 201 {beds:[...], count} (always a list, even for count=1). */
export const createBeds = async (wardId, roomId, body) => {
  try {
    const response = await apiClient.post(
      `${WARDS}/${enc(wardId)}/rooms/${enc(roomId)}/beds`,
      body,
    );
    return {
      success: true,
      data: {
        beds: response.data?.beds || [],
        count: response.data?.count ?? (response.data?.beds?.length || 0),
      },
    };
  } catch (error) {
    console.error('[BedInventoryAPI] create beds error:', error);
    // BED_BULK_LIMIT (422) carries `max` alongside {code, message} — pass it
    // through so the page can name the server's cap in the banner.
    const max = error?.response?.data?.detail?.max;
    const parsed = parseCatalogError(error, 'Failed to create beds.');
    return { success: false, ...parsed, ...(max != null ? { max } : {}) };
  }
};

export const updateBed = async (wardId, roomId, bedId, body) => {
  try {
    const response = await apiClient.patch(
      `${WARDS}/${enc(wardId)}/rooms/${enc(roomId)}/beds/${enc(bedId)}`,
      body,
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedInventoryAPI] update bed error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to update bed.') };
  }
};

export const deactivateBed = async (wardId, roomId, bedId) => {
  try {
    const response = await apiClient.delete(
      `${WARDS}/${enc(wardId)}/rooms/${enc(roomId)}/beds/${enc(bedId)}`,
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedInventoryAPI] deactivate bed error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to deactivate bed.') };
  }
};
